import type { Asset } from '@compass/client/types/asset';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Slider } from '@components/ui/slider';
import { assetsService } from '@services/assets.service';
import { formatDuration } from '@utils/date';
import { Download, Loader2, Pause, Play, RotateCcw, Volume2 } from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useStreamingAudio } from '@/hooks/useStreamingAudio';
import { useStore } from '@/store/store';
import type { OrgProgramContextOverride } from '@/types/context';
import { generateAudioFilename } from '@/utils/audioFormat';
import { downloadBlob } from '@/utils/download';

import { Button } from '../ui/button';
import { getAudioPlayerAvailability } from './audioPlayer.utils';

export interface AudioPlayerHandle {
  seekTo: (time: number) => void;
  stop: () => void;
}

interface AudioPlayerProps {
  assets: Asset[];
  caseRecordId: string;
  callId: string;
  callDetails?: { sid?: string } | null;
  className?: string;
  /** Whether the parent is still resolving which audio assets belong to the call */
  isLoadingAssets?: boolean;
  onTimeUpdate?: (currentTime: number) => void;
  /** Callback when audio starts playing */
  onPlay?: () => void;
  skipInterval?: number;
  caseCode?: string;
  /** Automatically start playback when audio is ready */
  autoPlay?: boolean;
  /** Optional org/program override for org-scoped pages like QC details */
  contextOverride?: OrgProgramContextOverride;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(
  (
    {
      caseRecordId,
      caseCode,
      callId,
      callDetails,
      className = '',
      isLoadingAssets = false,
      onTimeUpdate,
      onPlay,
      skipInterval = 5,
      assets,
      autoPlay = false,
      contextOverride,
    },
    ref,
  ) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(1);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const tabIdRef = useRef(`${Date.now()}-${Math.random()}`);
    const timeUpdateRafRef = useRef<number | null>(null);
    const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
    const selectedProgramId = useStore(state => state.selectedProgramId);
    const effectiveOrganizationId = contextOverride?.organizationId || selectedOrganizationId;
    const effectiveProgramId = contextOverride?.programId || selectedProgramId;

    // Select audio/mp3 asset first, fallback to first asset
    const recordingAsset =
      assets.find(asset => asset.content_type === 'audio/mp3')?.id || assets[0]?.id;
    const playerAvailability = getAudioPlayerAvailability({
      caseRecordId,
      isLoadingAssets,
      recordingAssetId: recordingAsset,
      organizationId: effectiveOrganizationId,
      programId: effectiveProgramId,
    });
    const unavailableMessage = !playerAvailability.hasCaseRecord
      ? 'Audio unavailable for this evaluation'
      : 'No audio recording available';

    // Stream audio via MSE
    const seekBarRef = useRef<HTMLDivElement>(null);

    const {
      audioRef,
      phase,
      progress,
      duration,
      bufferedEnd,
      error: streamError,
      retry,
    } = useStreamingAudio(caseRecordId, recordingAsset, callDetails?.sid, {
      organizationId: effectiveOrganizationId,
      programId: effectiveProgramId,
    });

    const isAudioReady = phase === 'streaming' || phase === 'ready';
    const isStreamLoading = phase === 'idle' || phase === 'connecting';

    // Attach audio element events
    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();
      };
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => {
        setIsPlaying(false);
        if (Number.isFinite(audio.duration)) {
          setCurrentTime(audio.duration);
        }
      };
      const handleTimeUpdate = () => {
        // Throttle state updates via rAF to avoid excessive re-renders
        if (timeUpdateRafRef.current) return;
        timeUpdateRafRef.current = requestAnimationFrame(() => {
          timeUpdateRafRef.current = null;
          const t = audio.currentTime;
          setCurrentTime(t);
          onTimeUpdate?.(t);
        });
      };
      const handleWaiting = () => setIsBuffering(true);
      const handleCanPlay = () => setIsBuffering(false);
      const handleError = () => {
        const err = audio.error;
        if (err) {
          console.error('Audio element error:', err.message);
          toast.error(err.message || 'Audio playback error');
        }
      };

      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('waiting', handleWaiting);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('error', handleError);

      return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('waiting', handleWaiting);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('error', handleError);
        if (timeUpdateRafRef.current) {
          cancelAnimationFrame(timeUpdateRafRef.current);
          timeUpdateRafRef.current = null;
        }
      };
    }, [audioRef, onTimeUpdate, onPlay]);

    // Auto-play when audio becomes ready
    useEffect(() => {
      if (autoPlay && isAudioReady && audioRef.current) {
        audioRef.current.play().catch(() => {
          // Autoplay may be blocked by browser
        });
      }
    }, [autoPlay, isAudioReady, audioRef]);

    // Reset state when call changes
    useEffect(() => {
      setIsPlaying(false);
      setCurrentTime(0);
    }, [callId]);

    // Expose imperative handle
    useImperativeHandle(
      ref,
      () => ({
        seekTo: (time: number) => {
          const audio = audioRef.current;
          if (audio) {
            audio.currentTime = time;
            setCurrentTime(time);
            onTimeUpdate?.(time);
          }
        },
        stop: () => {
          const audio = audioRef.current;
          if (audio) {
            audio.pause();
            setIsPlaying(false);
          }
        },
      }),
      [audioRef, onTimeUpdate],
    );

    // Cross-tab audio sync
    useEffect(() => {
      const storageListener = (e: StorageEvent) => {
        if (e.key === 'audio-playing' && e.newValue) {
          try {
            const data = JSON.parse(e.newValue);
            if (data.tabId !== tabIdRef.current && audioRef.current && !audioRef.current.paused) {
              audioRef.current.pause();
              setIsPlaying(false);
            }
          } catch {
            // Ignore parse errors
          }
        }
      };
      window.addEventListener('storage', storageListener);
      return () => window.removeEventListener('storage', storageListener);
    }, [audioRef]);

    const broadcastPlay = useCallback(() => {
      try {
        localStorage.setItem(
          'audio-playing',
          JSON.stringify({ tabId: tabIdRef.current, callId, timestamp: Date.now() }),
        );
      } catch {
        // Ignore storage errors
      }
    }, [callId]);

    // Controls
    const togglePlayPause = useCallback(() => {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) {
        broadcastPlay();
        audio.play().catch(err => console.error('Play failed:', err));
      } else {
        audio.pause();
      }
    }, [audioRef, broadcastPlay]);

    const restart = useCallback(() => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = 0;
      setCurrentTime(0);
      onTimeUpdate?.(0);
    }, [audioRef, onTimeUpdate]);

    const skip = useCallback(
      (seconds: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        const maxTime = Number.isFinite(audio.duration) ? audio.duration : Infinity;
        const newTime = Math.min(Math.max(0, audio.currentTime + seconds), maxTime);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
        onTimeUpdate?.(newTime);
      },
      [audioRef, onTimeUpdate],
    );

    const handleVolumeChange = useCallback(
      (value: number[]) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newVolume = value[0] / 100;
        audio.volume = newVolume;
        setVolume(newVolume);
      },
      [audioRef],
    );

    const handlePlaybackRateChange = useCallback(
      (rate: string) => {
        const audio = audioRef.current;
        if (!audio || !rate) return;
        const newRate = parseFloat(rate);
        if (!Number.isFinite(newRate) || newRate <= 0 || newRate > 4) return;
        audio.playbackRate = newRate;
        setPlaybackRate(newRate);
      },
      [audioRef],
    );

    // Seekbar pointer handling (click + drag to seek)
    const seekFromPointerEvent = useCallback(
      (e: React.PointerEvent) => {
        const audio = audioRef.current;
        if (!seekBarRef.current || !audio) return;
        const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
        if (dur === 0) return;
        const rect = seekBarRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const seekTime = pct * dur;
        audio.currentTime = seekTime;
        setCurrentTime(seekTime);
        onTimeUpdate?.(seekTime);
      },
      [audioRef, onTimeUpdate],
    );

    const handleSeekBarPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        seekFromPointerEvent(e);
      },
      [seekFromPointerEvent],
    );

    const handleSeekBarPointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        seekFromPointerEvent(e);
      },
      [seekFromPointerEvent],
    );

    // Derived state
    const formattedCurrentTime = useMemo(() => formatDuration(currentTime), [currentTime]);
    const formattedDuration = useMemo(() => formatDuration(duration), [duration]);

    const showLoadingIndicator =
      !playerAvailability.isUnavailable &&
      (playerAvailability.isPendingAssets ||
        playerAvailability.isPendingContext ||
        isStreamLoading);

    const arePlaybackControlsDisabled =
      playerAvailability.isUnavailable ||
      playerAvailability.isPendingAssets ||
      playerAvailability.isPendingContext ||
      !isAudioReady;

    const playbackPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Buffered range as percentage — use time-based when duration is known,
    // fall back to bytes-based during streaming when duration is still unknown
    const bufferedPercent =
      duration > 0 && bufferedEnd > 0
        ? Math.min((bufferedEnd / duration) * 100, 100)
        : progress.total && progress.total > 0
          ? Math.round((progress.received / progress.total) * 100)
          : 0;

    // Error state
    if (streamError || phase === 'error') {
      return (
        <div className={`text-center p-8 ${className}`}>
          <p className="text-destructive mb-2">Audio playback error</p>
          <p className="text-sm text-muted-foreground">{streamError}</p>
          <button
            onClick={retry}
            className="mt-4 px-4 py-2 text-sm bg-foreground text-primary-foreground rounded-md hover:bg-foreground"
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className={`bg-background ${className}`}>
        <div className="px-4 py-2 flex flex-col gap-2">
          {/* Seek bar with buffered range visualization */}
          <div className="relative overflow-hidden" style={{ height: '40px' }}>
            {playerAvailability.isUnavailable ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                {unavailableMessage}
              </div>
            ) : showLoadingIndicator ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : isAudioReady ? (
              <div className="absolute inset-0 flex items-center">
                <div
                  ref={seekBarRef}
                  className={`relative w-full h-6 flex items-center group ${arePlaybackControlsDisabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                  onPointerDown={handleSeekBarPointerDown}
                  onPointerMove={handleSeekBarPointerMove}
                  role="slider"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(playbackPercent)}
                >
                  {/* Track background */}
                  <div className="absolute left-0 right-0 h-1 rounded-full bg-secondary" />
                  {/* Buffered range */}
                  <div
                    className="absolute left-0 h-1 rounded-full bg-primary/25 transition-[width] duration-300"
                    style={{ width: `${bufferedPercent}%` }}
                  />
                  {/* Played range */}
                  <div
                    className="absolute left-0 h-1 rounded-full bg-primary"
                    style={{ width: `${playbackPercent}%` }}
                  />
                  {/* Thumb */}
                  <div
                    className="absolute top-1/2 h-3.5 w-3.5 rounded-full border-2 border-primary bg-background opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                    style={{
                      left: `${playbackPercent}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>
                {isBuffering && (
                  <span className="text-xs text-muted-foreground ml-2 whitespace-nowrap">
                    Buffering...
                  </span>
                )}
              </div>
            ) : null}
          </div>

          {/* Controls row */}
          <div className="flex justify-between items-center">
            <div className="flex items-center justify-between">
              {/* Left side - playback controls */}
              <div className="flex items-center gap-1">
                {/* Restart button */}
                <Button
                  onClick={restart}
                  variant="ghost"
                  size="sm"
                  disabled={arePlaybackControlsDisabled}
                  className="p-1 hover:bg-transparent hover:text-primary"
                  title="Restart"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>

                {/* Skip backward button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => skip(-skipInterval)}
                  disabled={arePlaybackControlsDisabled}
                  className="p-1 hover:bg-transparent hover:text-primary relative"
                  title={`Rewind ${skipInterval}s`}
                >
                  <RotateCcw className="h-4 w-4" />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold pointer-events-none">
                    {skipInterval}
                  </span>
                </Button>

                <Button
                  data-analytics-id="call:audio:play"
                  variant="default"
                  size="sm"
                  onClick={togglePlayPause}
                  disabled={arePlaybackControlsDisabled}
                  className="p-1 rounded-full w-8 h-8 flex items-center justify-center"
                >
                  {showLoadingIndicator || isBuffering ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" style={{ marginLeft: '2px' }} />
                  )}
                </Button>

                {/* Skip forward button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => skip(skipInterval)}
                  disabled={arePlaybackControlsDisabled}
                  className="p-1 hover:bg-transparent relative hover:text-primary"
                  title={`Forward ${skipInterval}s`}
                >
                  <RotateCcw className="h-4 w-4 scale-x-[-1]" />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold pointer-events-none">
                    {skipInterval}
                  </span>
                </Button>
              </div>
              <div className="flex items-center ml-2">
                <Volume2 className="h-4 w-4" />
                <Slider
                  value={[volume * 100]}
                  onValueChange={handleVolumeChange}
                  disabled={arePlaybackControlsDisabled}
                  max={100}
                  step={1}
                  className="w-20 h-1 ml-1 cursor-move"
                />
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Download button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (
                    recordingAsset &&
                    !isDownloading &&
                    effectiveOrganizationId &&
                    effectiveProgramId
                  ) {
                    setIsDownloading(true);
                    const currentCaseCode = caseCode;
                    const currentCaseId = caseRecordId;
                    const currentCallId = callId;
                    const currentAssetId = recordingAsset;
                    try {
                      const context = {
                        organizationId: effectiveOrganizationId,
                        programId: effectiveProgramId,
                        caseRecordId,
                      };
                      const hasSid = !!callDetails?.sid;
                      const blob = hasSid
                        ? await assetsService.downloadAsset(context, currentAssetId, 'audio')
                        : await assetsService.downloadAssetFromArcus(
                            context,
                            currentAssetId,
                            'audio',
                          );
                      const filename = generateAudioFilename(
                        currentCaseCode,
                        currentCaseId,
                        currentCallId,
                        blob.type,
                      );
                      downloadBlob(blob, filename);
                      toast.success('Audio downloaded successfully');
                    } catch (error) {
                      console.error('Download failed:', error);
                      toast.error('Failed to download audio');
                    } finally {
                      setIsDownloading(false);
                    }
                  }
                }}
                disabled={
                  playerAvailability.isUnavailable ||
                  playerAvailability.isPendingContext ||
                  !recordingAsset ||
                  !isAudioReady ||
                  isDownloading
                }
                className="rounded transition-colors disabled:opacity-50 relative p-2"
                title={isDownloading ? 'Downloading...' : 'Download Audio'}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
              {/* Playback speed */}
              <Select
                value={playbackRate.toString()}
                onValueChange={handlePlaybackRateChange}
                disabled={arePlaybackControlsDisabled}
              >
                <SelectTrigger className="h-6 text-sm border-none bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.75">0.75x</SelectItem>
                  <SelectItem value="1">1x</SelectItem>
                  <SelectItem value="1.25">1.25x</SelectItem>
                  <SelectItem value="1.5">1.5x</SelectItem>
                </SelectContent>
              </Select>

              <div className="text-sm whitespace-nowrap">
                {formattedCurrentTime} / {formattedDuration}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

AudioPlayer.displayName = 'AudioPlayer';
